import { API } from "@js/api";
import type { RootState } from "@js/store";
import type {
  EmployerBid,
  InitialOffer,
  SaveOptionalFeesProps,
} from "@js/types/jobs";
import { parseListQueryParam, typeGuard } from "@js/utils";

import {
  getAllFetchBidsArgs,
  patchBidOnHideToggle,
  patchBidOnMatcherRecommendedToggle,
  patchBidOnReactionUpdate,
  patchBidOnRequestIDV,
  patchBidOnViewedUpdate,
  patchBidsOnHideToggle,
  patchBidsOnMatcherRecommendedToggle,
  patchBidsOnReactionUpdate,
  patchBidsOnViewedUpdate,
  patchBidsRequestIDV,
} from "./helpers";
import type {
  BidInterviewIntendedParams,
  FetchBidParams,
  FetchBidsParams,
  FetchBidsResponse,
  FetchInitialOfferParams,
  GetBidRecommendedLocationsResponse,
  HideProposalParams,
  HideProposalsParams,
  MarkBidAsViewedParams,
  MarkCalendarViewedParams,
  SendBidInterviewScheduleLinkParams,
  UnrejectBidParams,
  UpdateBidReactionParams,
  UpdateBidStatusParams,
  UpdateFeedbackParams,
  UpdateInterviewInBulkPayload,
  UpdateMatcherRecommendationParams,
} from "./types";

export const bidApi = API.injectEndpoints({
  endpoints: (build) => ({
    unRejectBid: build.mutation<void, UnrejectBidParams>({
      query: ({ bidId }) => ({
        url: `/employer_bids/${bidId}/unreject/`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "EmployerBids", id: arg.bidId },
      ],
    }),

    fetchBids: build.query<FetchBidsResponse, FetchBidsParams>({
      query: (args) => {
        const ids = parseListQueryParam(args?.ids);
        const skills = parseListQueryParam(args?.skills);
        const top_skills = parseListQueryParam(args?.top_skills);

        return {
          url: "/employer_bids/",
          method: "GET",
          params: {
            ...args,
            ids,
            top_skills,
            skills,
          },
        };
      },
      providesTags: [{ type: "EmployerBids", id: "LIST" }],
    }),

    markBidAsViewed: build.mutation<void, MarkBidAsViewedParams>({
      query: ({ bidId }) => ({
        url: `/employer_bids/${bidId}/mark_as_viewed/`,
        method: "POST",
      }),
      async onQueryStarted({ bidId }, { dispatch, getState, queryFulfilled }) {
        const state = getState() as RootState;
        const patchResults: Array<{ undo: () => void }> = [];

        const allFetchBidsArgs = getAllFetchBidsArgs(state);
        allFetchBidsArgs.forEach((fetchBidsArg) => {
          const bidsPatchResult = dispatch(
            bidApi.util.updateQueryData("fetchBids", fetchBidsArg, (draft) => {
              patchBidsOnViewedUpdate({ draft, bidId });
            }),
          );

          patchResults.push(bidsPatchResult);
        });

        const bidPatchResult = dispatch(
          bidApi.util.updateQueryData("fetchBid", { bidId }, (draft) => {
            patchBidOnViewedUpdate({ bid: draft });
          }),
        );

        patchResults.push(bidPatchResult);

        try {
          await queryFulfilled;
        } catch (e) {
          if (
            typeGuard<unknown, { error: unknown }>(e, "error") &&
            typeGuard<unknown, { status: number }>(e.error, "status") &&
            e.error.status === 400
          ) {
            // do not undo patches in case of status 400 as it means bid is already viewed ...
            // ... so we can leave UI updated
            return;
          }

          patchResults.forEach((patchResult) => patchResult.undo());
        }
      },
    }),

    markBidAsHired: build.mutation<void, { bidId: number }>({
      query: ({ bidId }) => ({
        url: `/employer_bids/${bidId}/mark_as_hired/`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "EmployerBids", id: arg.bidId },
        { type: "EmployerBids", id: "LIST" },
      ],
    }),

    requestIDV: build.mutation<void, { bidId: number }>({
      query: ({ bidId }) => ({
        url: `/employer_bids/${bidId}/request_idv/`,
        method: "POST",
      }),
      async onQueryStarted({ bidId }, { dispatch, getState, queryFulfilled }) {
        const state = getState() as RootState;
        const patchResults: Array<{ undo: () => void }> = [];

        const allFetchBidsArgs = getAllFetchBidsArgs(state);
        allFetchBidsArgs.forEach((fetchBidsArg) => {
          const bidsPatchResult = dispatch(
            bidApi.util.updateQueryData("fetchBids", fetchBidsArg, (draft) => {
              patchBidsRequestIDV({ draft, bidId });
            }),
          );

          patchResults.push(bidsPatchResult);
        });

        const bidPatchResult = dispatch(
          bidApi.util.updateQueryData("fetchBid", { bidId }, (draft) => {
            patchBidOnRequestIDV({ bid: draft });
          }),
        );

        patchResults.push(bidPatchResult);

        try {
          await queryFulfilled;
        } catch (e) {
          if (
            typeGuard<unknown, { error: unknown }>(e, "error") &&
            typeGuard<unknown, { status: number }>(e.error, "status") &&
            e.error.status === 400
          ) {
            // do not undo patches in case of status 400 as it means bid is already viewed ...
            // ... so we can leave UI updated
            return;
          }

          patchResults.forEach((patchResult) => patchResult.undo());
        }
      },
    }),

    updateEmployerBidSection: build.mutation<void, UpdateBidStatusParams>({
      query: ({ bidId, status }) => ({
        url: `/employer_bids/${bidId}/update_status/`,
        method: "POST",
        data: { status },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "EmployerBids", id: arg.bidId },
      ],
    }),

    bulkSendApplicationData: build.mutation<void, { pks: number[] }>({
      query: (data) => ({
        url: `/employer_bids/bulk_send_application_data/`,
        method: "POST",
        data,
      }),
    }),

    updateEmployerBidInterviewSection: build.mutation<
      void,
      { bidId: number; status: EnumType<typeof ENUMS.JobInterviewStatus> }
    >({
      query: ({ bidId, status }) => ({
        url: `/employer_bids/${bidId}/update_interview_status/`,
        method: "POST",
        data: { status },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "EmployerBids", id: arg.bidId },
        { type: "EmployerBids", id: "LIST" },
      ],
    }),

    resendEmployerBidInterview: build.mutation<void, { bidId: number }>({
      query: ({ bidId }) => ({
        url: `/employer_bids/${bidId}/resend_ai_interview_request/`,
        method: "POST",
        data: {},
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "EmployerBids", id: arg.bidId },
        { type: "EmployerBids", id: "LIST" },
      ],
    }),

    updateEmployerBidFeedback: build.mutation<void, UpdateFeedbackParams>({
      query: ({ bidId, feedback }) => ({
        url: `/employer_bids/${bidId}/update_feedback/`,
        method: "POST",
        data: { ...feedback },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "EmployerBids", id: arg.bidId },
      ],
    }),

    updateEmployerBidInBulk: build.mutation<
      {
        id: number;
        result: string;
        status: string;
      }[],
      UpdateInterviewInBulkPayload
    >({
      query: (data) => ({
        url: `/employer_bids/update_status_bulk/`,
        method: "POST",
        data,
      }),
      invalidatesTags: [{ type: "EmployerBids" as const, id: "LIST" }],
    }),

    fetchBid: build.query<EmployerBid, FetchBidParams>({
      query: ({ bidId }) => ({
        url: `/employer_bids/${bidId}/`,
        method: "GET",
      }),
      providesTags: (_result, _error, arg) => [
        { type: "EmployerBids", id: arg.bidId },
      ],
      keepUnusedDataFor: 1,
    }),

    markCalendarViewed: build.mutation<void, MarkCalendarViewedParams>({
      query: ({ bidId }) => ({
        url: `/employer_bids/${bidId}/mark_calendar_as_viewed/`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "EmployerBids", id: arg.bidId },
      ],
    }),

    saveOptionalFees: build.mutation<void, SaveOptionalFeesProps>({
      query: ({ bidId, fees }) => ({
        url: `/employer_bids/${bidId}/update_fees/`,
        method: "POST",
        data: fees,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "EmployerBids", id: arg.bidId },
      ],
    }),

    hideProposal: build.mutation<void, HideProposalParams>({
      query: ({ bidId, isHidden }) => ({
        url: `/employer_bids/${bidId}/change_visibility/`,
        method: "POST",
        data: { is_hidden: isHidden },
      }),
      async onQueryStarted(
        { bidId, isHidden },
        { dispatch, getState, queryFulfilled },
      ) {
        const state = getState() as RootState;
        const patchResults: Array<{ undo: () => void }> = [];

        const allFetchBidsArgs = getAllFetchBidsArgs(state);
        allFetchBidsArgs.forEach((fetchBidsArg) => {
          const bidsPatchResult = dispatch(
            bidApi.util.updateQueryData("fetchBids", fetchBidsArg, (draft) => {
              patchBidsOnHideToggle({ draft, bidId, isHidden });
            }),
          );

          patchResults.push(bidsPatchResult);
        });

        const bidPatchResult = dispatch(
          bidApi.util.updateQueryData("fetchBid", { bidId }, (draft) => {
            patchBidOnHideToggle({ bid: draft, isHidden });
          }),
        );

        patchResults.push(bidPatchResult);

        try {
          await queryFulfilled;
        } catch (_e) {
          patchResults.forEach((patchResult) => patchResult.undo());
        }
      },
    }),

    hideProposals: build.mutation<void, HideProposalsParams>({
      query: ({ bidIds, isHidden }) => ({
        url: `/employer_bids/change_visibility_bulk/`,
        method: "POST",
        data: { is_hidden: isHidden, pks: bidIds },
      }),
      invalidatesTags: ["EmployerBids"],
    }),

    updateBidReaction: build.mutation<void, UpdateBidReactionParams>({
      query: ({ bidId, reaction }) => ({
        url: `/employer_bids/${bidId}/update_reaction/`,
        method: "POST",
        data: { reaction },
      }),
      async onQueryStarted(
        { bidId, reaction },
        { dispatch, getState, queryFulfilled },
      ) {
        const state = getState() as RootState;
        const patchResults: Array<{ undo: () => void }> = [];

        const allFetchBidsArgs = getAllFetchBidsArgs(state);
        allFetchBidsArgs.forEach((fetchBidsArg) => {
          const bidsPatchResult = dispatch(
            bidApi.util.updateQueryData("fetchBids", fetchBidsArg, (draft) => {
              patchBidsOnReactionUpdate({ draft, bidId, reaction });
            }),
          );

          patchResults.push(bidsPatchResult);
        });

        const bidPatchResult = dispatch(
          bidApi.util.updateQueryData("fetchBid", { bidId }, (draft) => {
            patchBidOnReactionUpdate({ bid: draft, reaction });
          }),
        );

        patchResults.push(bidPatchResult);

        try {
          await queryFulfilled;
        } catch (_e) {
          patchResults.forEach((patchResult) => patchResult.undo());
        }
      },
    }),

    bidInterviewIntended: build.mutation<void, BidInterviewIntendedParams>({
      query: ({ bidId }) => ({
        url: `/employer_bids/${bidId}/interview_intended/`,
        method: "POST",
      }),
    }),

    updateMatcherRecommendation: build.mutation<
      void,
      UpdateMatcherRecommendationParams
    >({
      query: ({ bidId, isRecommended }) => ({
        url: `/employer_bids/${bidId}/update_matcher_recommendation/`,
        method: "POST",
        data: { recommended_by_matcher: isRecommended },
      }),
      invalidatesTags: (_result, _error, arg) => {
        const shouldInvalidateBid = !arg.isRecommended;
        if (!shouldInvalidateBid) {
          return [{ type: "EmployerBids", id: "LIST" }];
        }

        return [
          { type: "EmployerBids", id: "LIST" },
          { type: "EmployerBids", id: arg.bidId },
        ];
      },
      async onQueryStarted(
        { bidId, isRecommended },
        { dispatch, getState, queryFulfilled },
      ) {
        const state = getState() as RootState;
        const patchResults: Array<{ undo: () => void }> = [];

        const allFetchBidsArgs = getAllFetchBidsArgs(state);
        allFetchBidsArgs.forEach((fetchBidsArg) => {
          const bidsPatchResult = dispatch(
            bidApi.util.updateQueryData("fetchBids", fetchBidsArg, (draft) => {
              patchBidsOnMatcherRecommendedToggle({
                draft,
                bidId,
                isRecommended,
              });
            }),
          );

          patchResults.push(bidsPatchResult);
        });

        const bidPatchResult = dispatch(
          bidApi.util.updateQueryData("fetchBid", { bidId }, (draft) => {
            patchBidOnMatcherRecommendedToggle({ bid: draft, isRecommended });
          }),
        );

        patchResults.push(bidPatchResult);

        try {
          await queryFulfilled;
        } catch (_e) {
          patchResults.forEach((patchResult) => patchResult.undo());
        }
      },
    }),
    fetchInitialOffer: build.query<InitialOffer, FetchInitialOfferParams>({
      query: ({ bidId, isBulk }) => ({
        url: `/employer_bids/${bidId}/offer_initial_data`,
        method: "GET",
        params: isBulk ? { is_bulk: "true" } : undefined,
      }),
    }),
    getBidRecommendedLocations: build.query<
      GetBidRecommendedLocationsResponse,
      {
        jobId: number;
      }
    >({
      query: ({ jobId }) => ({
        url: `/employer_jobs_minimal/${jobId}/bids_locations`,
        method: "GET",
      }),
      providesTags: (_result, _error, arg) => [
        { type: "JobBidsLocations", id: arg.jobId },
      ],
    }),

    retryInterview: build.mutation<void, { bidId: number }>({
      query: ({ bidId }) => ({
        url: `/employer_bids/${bidId}/retry_interview/`,
        method: "POST",
      }),
      invalidatesTags: (_res, _err, arg) => [
        { type: "EmployerBids", id: arg.bidId },
        { type: "EmployerBids", id: "LIST" },
      ],
    }),

    sendInterviewScheduleLink: build.mutation<
      void,
      SendBidInterviewScheduleLinkParams
    >({
      query: ({ bidId, ...data }) => ({
        url: `/employer_bids/${bidId}/send_interview_schedule_link/`,
        method: "POST",
        data,
      }),
      invalidatesTags: (_res, _err, arg) => [
        { type: "EmployerBids", id: arg.bidId },
        { type: "EmployerBids", id: "LIST" },
      ],
    }),

    getInterviewRecordingUrl: build.query<
      { interview_recording_url: string | null },
      {
        bidId: number;
      }
    >({
      keepUnusedDataFor: 100000,
      query: ({ bidId }) => ({
        url: `/employer_bids/${bidId}/air_interview_video_link`,
        method: "GET",
      }),
    }),
  }),
});

export const {
  useUnRejectBidMutation,
  useBidInterviewIntendedMutation,
  useFetchBidsQuery,
  useHideProposalMutation,
  useHideProposalsMutation,
  useMarkBidAsViewedMutation,
  useUpdateBidReactionMutation,
  useUpdateEmployerBidFeedbackMutation,
  useUpdateEmployerBidInBulkMutation,
  useSaveOptionalFeesMutation,
  useUpdateEmployerBidSectionMutation,
  useUpdateEmployerBidInterviewSectionMutation,
  useResendEmployerBidInterviewMutation,
  useUpdateMatcherRecommendationMutation,
  useFetchBidQuery,
  useFetchInitialOfferQuery,
  useGetBidRecommendedLocationsQuery,
  useRetryInterviewMutation,
  useSendInterviewScheduleLinkMutation,
  useBulkSendApplicationDataMutation,
  useGetInterviewRecordingUrlQuery,
  useRequestIDVMutation,
  useMarkBidAsHiredMutation,
} = bidApi;

export const useFetchInitialOfferQueryState =
  bidApi.endpoints.fetchInitialOffer.useQueryState;
